'use client'
import React, { useState, useEffect, useRef, useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import { useFormikContext } from 'formik'
import { loadSector, loadSeats } from '~/state/modules/planimetry'
import { Container, Box, CircularProgress, Typography } from '@material-ui/core'
import SeatPlanner from './components/SeatPlanner'
import ControlPanel from './components/ControlPanel'
import { SeatStatus, PositionDescriptor } from 'config/constants'
import { updateSeatsGrid } from './lib/utils'

export default function SeatSettings() {
  const { sectorId } = useParams()
  const dispatch = useDispatch()
  const { pendingSector, pendingSeats } = useSelector(
    (state) => state.planimetry
  )
  const [seats, setSeats] = useState([])
  const [seatGrid, setSeatGrid] = useState([])
  const [rows, setRows] = useState()
  const [columns, setColumns] = useState()
  const [rowDescriptor, setRowDescriptor] = useState(PositionDescriptor.Numeric)
  const [columnDescriptor, setColumnDescriptor] = useState(
    PositionDescriptor.Numeric
  )
  const [selectedStatus, setSelectedStatus] = useState(SeatStatus.Empty)
  const seatPlannerRef = useRef(null)
  const { setFieldValue } = useFormikContext()

  // Single seat status updater (defined before callback)
  const updateSeatStatus = useCallback(
    (row, column, status) => {
      const seat = seatGrid[row - 1]?.[column - 1]
      if (!seat || seat.status === status) return
      const updatedSeat = { ...seat, status }
      setSeats((prev) =>
        prev.map((s) =>
          s.row === row && s.column === column ? updatedSeat : s
        )
      )
      setSeatGrid((prev) => {
        const newGrid = prev.map((r) => [...r])
        newGrid[row - 1][column - 1] = updatedSeat
        return newGrid
      })
    },
    [seatGrid]
  )

  // Ref to always read latest selectedStatus
  const selectedStatusRef = useRef(selectedStatus)
  useEffect(() => {
    selectedStatusRef.current = selectedStatus
  }, [selectedStatus])

  // Stable click handler for seats
  const handleSeatClick = useCallback(
    (row, column) => {
      updateSeatStatus(row, column, selectedStatusRef.current)
    },
    [updateSeatStatus]
  )

  // Update entire row
  const updateRowStatus = (row, status) => {
    if (row < 1 || row > rows) return
    const updatedSeats = seats.map((s) =>
      s.row === row ? { ...s, status } : s
    )
    setSeats(updatedSeats)
    setSeatGrid((prev) => {
      const newGrid = prev.map((r) => [...r])
      newGrid[row - 1] = newGrid[row - 1].map((seat) =>
        seat ? { ...seat, status } : seat
      )
      return newGrid
    })
  }

  // Update entire column
  const updateColumnStatus = (column, status) => {
    if (column < 1 || column > columns) return
    const updatedSeats = seats.map((s) =>
      s.column === column ? { ...s, status } : s
    )
    setSeats(updatedSeats)
    setSeatGrid((prev) => {
      const newGrid = prev.map((r) => [...r])
      for (let r = 0; r < rows; r++) {
        if (newGrid[r][column - 1]) {
          newGrid[r][column - 1] = { ...newGrid[r][column - 1], status }
        }
      }
      return newGrid
    })
  }

  // Load sector and seats
  useEffect(() => {
    async function fetchSectorAndSeats() {
      const sector = await dispatch(loadSector(sectorId))
      setRows(sector.Row)
      setColumns(sector.Column)
      setRowDescriptor(sector.RowDescriptor)
      setColumnDescriptor(sector.ColumnDescriptor)

      const loadedSeats = await dispatch(loadSeats(sectorId))
      if (loadedSeats && loadedSeats.length > 0) {
        setSeats(loadedSeats)
        initializeSeatGrid(loadedSeats, sector.Row, sector.Column)
      } else {
        initializeSeats(sector.Row, sector.Column)
      }
    }
    fetchSectorAndSeats()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, sectorId])

  // Sync with Formik
  useEffect(() => {
    setFieldValue('Seats', seats)
    setFieldValue('Row', rows)
    setFieldValue('Column', columns)
  }, [seats, rows, columns, setFieldValue])

  // Initialize seats list
  const initializeSeats = (numRows, numColumns) => {
    const newSeats = Array.from({ length: numRows * numColumns }, (_, idx) => ({
      row: Math.floor(idx / numColumns) + 1,
      column: (idx % numColumns) + 1,
      status: SeatStatus.Available
    }))
    setSeats(newSeats)
    initializeSeatGrid(newSeats, numRows, numColumns)
  }

  // Initialize 2D grid
  const initializeSeatGrid = (seatList, numRows, numColumns) => {
    const grid = Array.from({ length: numRows }, () =>
      Array(numColumns).fill(null)
    )
    seatList.forEach((seat) => {
      grid[seat.row - 1][seat.column - 1] = seat
    })
    setSeatGrid(grid)
  }

  // Add/remove rows/columns
  const addRow = () => {
    const newRows = rows + 1
    setRows(newRows)
    const updated = updateSeatsGrid(newRows, columns, seats)
    setSeats(updated)
    initializeSeatGrid(updated, newRows, columns)
  }
  const removeRow = () => {
    if (rows > 1) {
      const newRows = rows - 1
      setRows(newRows)
      const updated = updateSeatsGrid(newRows, columns, seats)
      setSeats(updated)
      initializeSeatGrid(updated, newRows, columns)
    }
  }
  const addColumn = () => {
    const newCols = columns + 1
    setColumns(newCols)
    const updated = updateSeatsGrid(rows, newCols, seats)
    setSeats(updated)
    initializeSeatGrid(updated, rows, newCols)
    setTimeout(() => seatPlannerRef.current?.checkScroll(), 0)
  }
  const removeColumn = () => {
    if (columns > 1) {
      const newCols = columns - 1
      setColumns(newCols)
      const updated = updateSeatsGrid(rows, newCols, seats)
      setSeats(updated)
      initializeSeatGrid(updated, rows, newCols)
      setTimeout(() => seatPlannerRef.current?.checkScroll(), 0)
    }
  }

  const nonEmptySeats = seats.filter((seat) => seat.status !== SeatStatus.Empty)
  const totalNonEmpty = nonEmptySeats.length

  const statusCounts = seats.reduce((acc, seat) => {
    const prev = acc[seat.status]?.count || 0
    const count = prev + 1
    let percent = 0
    if (seat.status !== SeatStatus.Empty && totalNonEmpty > 0) {
      percent = (count / totalNonEmpty) * 100
      percent = percent % 1 === 0 ? percent.toFixed(0) : percent.toFixed(2)
      percent = Number(percent)
    }
    acc[seat.status] = { count, percent }
    return acc
  }, {})

  Object.keys(statusCounts).forEach((status) => {
    const { count, percent } = statusCounts[status]
    if (status === SeatStatus.Empty.toString()) {
      statusCounts[status] = `${count}`
    } else {
      statusCounts[status] = `${count} (${percent}%)`
    }
  })

  return (
    <Container maxWidth='lg'>
      <Box sx={{ my: 2 }}>
        <ControlPanel
          rows={rows}
          columns={columns}
          columnDescriptor={columnDescriptor}
          rowDescriptor={rowDescriptor}
          addRow={addRow}
          removeRow={removeRow}
          addColumn={addColumn}
          removeColumn={removeColumn}
          selectedStatus={selectedStatus}
          setSelectedStatus={setSelectedStatus}
          updateRowStatus={updateRowStatus}
          updateColumnStatus={updateColumnStatus}
          statusCounts={statusCounts}
        />
        <br />
        {pendingSector || pendingSeats || !seatGrid || seatGrid.length === 0 ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center'
            }}>
            <CircularProgress size={24} style={{ marginRight: '.5rem' }} />
            <Typography>Cargando...</Typography>
          </Box>
        ) : (
          <SeatPlanner
            ref={seatPlannerRef}
            seatGrid={seatGrid}
            rows={rows}
            columns={columns}
            columnDescriptor={columnDescriptor}
            rowDescriptor={rowDescriptor}
            onSeatClick={handleSeatClick}
          />
        )}
      </Box>
    </Container>
  )
}
